import { useState, useEffect, useRef } from "react";
import { FAQ_SECTIONS } from "./faqData";

export default function FAQTab() {
  const [search,        setSearch]        = useState("");
  const [openItems,     setOpenItems]     = useState<Record<string, boolean>>({});
  const [activeSection, setActiveSection] = useState(FAQ_SECTIONS[0].id);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const filtered = FAQ_SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter(
      (item) =>
        !search ||
        item.q.toLowerCase().includes(search.toLowerCase()) ||
        item.a.toLowerCase().includes(search.toLowerCase()),
    ),
  })).filter((s) => s.items.length > 0);

  useEffect(() => {
    if (search && filtered.length > 0) setActiveSection(filtered[0].id);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key: string) =>
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));

  const scrollTo = (id: string) =>
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="sh-layout">

      {/* Left nav */}
      <div className="sh-nav-card">
        <div className="sh-search-row">
          <i className="fas fa-magnifying-glass sh-search-icon" />
          <input
            className="sh-search-input"
            placeholder="Search FAQs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="sh-search-clear" onClick={() => setSearch("")}>
              <i className="fas fa-xmark" />
            </button>
          )}
        </div>

        <div className="sh-nav__label">Sections</div>

        <div className="sh-nav__list">
          {FAQ_SECTIONS.map((s) => {
            const matchCount = !search
              ? s.items.length
              : s.items.filter(
                  (item) =>
                    item.q.toLowerCase().includes(search.toLowerCase()) ||
                    item.a.toLowerCase().includes(search.toLowerCase()),
                ).length;
            if (search && matchCount === 0) return null;
            return (
              <button
                key={s.id}
                className={`sh-nav__btn ${activeSection === s.id ? "sh-nav__btn--active" : ""}`}
                onClick={() => { setActiveSection(s.id); scrollTo(s.id); }}
              >
                <span>{s.title}</span>
                <span className="sh-nav__count">{matchCount}</span>
              </button>
            );
          })}
        </div>

        <div className="sh-nav__tip">
          <i className="fas fa-lightbulb" />
          {" "}Click a section to jump to it, or search above to filter questions.
        </div>
      </div>

      {/* Main content */}
      <div className="sh-main">
        {filtered.length === 0 ? (
          <div className="sh-empty">
            <div className="sh-empty__title">No results found</div>
            <div className="sh-empty__sub">Try a different search term or clear the search box.</div>
          </div>
        ) : (
          filtered.map((section) => (
            <div
              key={section.id}
              className="sh-section-card"
              ref={(el) => { sectionRefs.current[section.id] = el; }}
            >
              <div className="sh-section-card__head">
                <span className="sh-section-card__title">{section.title}</span>
                <span className="sh-section-card__count">{section.items.length} articles</span>
              </div>
              <div className="sh-accordion">
                {section.items.map((item, idx) => {
                  const key  = `${section.id}:${idx}`;
                  const open = !!openItems[key];
                  return (
                    <div key={key} className={`sh-accordion__item ${open ? "sh-accordion__item--open" : ""}`}>
                      <button className="sh-accordion__trigger" onClick={() => toggle(key)}>
                        <div className="sh-accordion__trigger-left">
                          <span className="sh-accordion__icon-box">
                            <i className="fas fa-circle-question" />
                          </span>
                          <span className="sh-accordion__question">{item.q}</span>
                        </div>
                        <i className={`fas ${open ? "fa-chevron-up" : "fa-chevron-down"} sh-accordion__chevron`} />
                      </button>
                      {open && (
                        <div className="sh-accordion__answer">
                          <p>{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
